import React, { useState, useEffect } from "react"
import axios from "axios"
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  Avatar,
  CircularProgress,
  Alert,
} from "@mui/material"
import {
  AttachMoney,
  Search,
  FilterList,
  CheckCircle,
  Schedule,
  Cancel,
  Person,
} from "@mui/icons-material"
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts"
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdvanceSalarySection = ({ data = [], period = 'yearly' }) => {
  const [salaryRequests, setSalaryRequests] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("All")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  // Fetch data or use prop data
  useEffect(() => {
    if (data && data.length > 0) {
      const mappedSalaryRequests = data.map(req => ({
        id: req.id,
        employeeName: req.name || req.employeeName,
        employeeId: req.employee_id || req.employeeId,
        reason: req.reason || `Advance Salary - ₹${req.salaryAmount}`,
        status: req.status || 'Pending',
        requestDate: req.created_at || req.createdAt || new Date().toISOString(),
        processedDate: req.updated_at || req.updatedAt,
        department: req.department,
        candidateEmail: req.candidateEmail,
        salaryAmount: req.salaryAmount,
      }))
      setSalaryRequests(mappedSalaryRequests)
      setIsLoading(false)
    } else {
      fetchData()
    }
  }, [data])

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      setIsLoading(true)
      const response = await axios.get(`${API_URL}/api/employee-report/summary?period=${period}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })

      if (response.data.status === "success" && response.data.data) {
        const salaryData = response.data.data.salaryRequests || [];
        const mappedSalaryRequests = salaryData.map(req => ({
          id: req.id,
          employeeName: req.name || req.employeeName,
          employeeId: req.employee_id || req.employeeId,
          reason: req.reason || `Advance Salary - ₹${req.salaryAmount}`,
          status: req.status || 'Pending',
          requestDate: req.created_at || req.createdAt || new Date().toISOString(),
          processedDate: req.updated_at || req.updatedAt,
          department: req.department,
          candidateEmail: req.candidateEmail,
          salaryAmount: req.salaryAmount,
        }))
        setSalaryRequests(mappedSalaryRequests)
      } else {
        throw new Error('Failed to load salary requests')
      }
    } catch (err) {
      setError("Failed to fetch salary requests. Please try again.")
      //console.error("Error fetching data:", err)
    } finally {
      setIsLoading(false)
    }
  }

  // Filter requests - only show pending status for table
  const pendingRequests = salaryRequests.filter(request => 
    request.status?.toLowerCase() === 'pending'
  )

  // Apply search filter to pending requests
  const filteredRequests = pendingRequests.filter(request => {
    const matchesSearch = 
      request.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.department?.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesSearch
  })

  // Pagination
  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Status chip styling
  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase() || 'pending'
    const statusConfig = {
      approved: { color: 'success', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      pending: { color: 'warning', icon: <Schedule sx={{ fontSize: 16 }} /> },
      rejected: { color: 'error', icon: <Cancel sx={{ fontSize: 16 }} /> },
      cancelled: { color: 'default', icon: <Cancel sx={{ fontSize: 16 }} /> }
    }
    
    const config = statusConfig[statusLower] || statusConfig.pending
    
    return (
      <Chip
        label={status || 'Pending'}
        color={config.color}
        size="small"
        icon={config.icon}
        sx={{ fontWeight: 500 }}
      />
    )
  }

  // Statistics calculation
  const getRequestStats = () => {
    const stats = {
      total: salaryRequests.length,
      approved: salaryRequests.filter(r => r.status?.toLowerCase() === 'approved').length,
      pending: salaryRequests.filter(r => r.status?.toLowerCase() === 'pending').length,
      rejected: salaryRequests.filter(r => r.status?.toLowerCase() === 'rejected').length,
      totalAmount: salaryRequests.reduce((sum, req) => sum + (req.salaryAmount || 0), 0),
      approvedAmount: salaryRequests.filter(r => r.status?.toLowerCase() === 'approved').reduce((sum, req) => sum + (req.salaryAmount || 0), 0),
    }
    return stats
  }

  const stats = getRequestStats()

  // Chart data preparation
  const pieData = [
    { name: 'Approved', value: stats.approved, color: '#10b981' },
    { name: 'Pending', value: stats.pending, color: '#f59e0b' },
    { name: 'Rejected', value: stats.rejected, color: '#ef4444' },
  ].filter(item => item.value > 0)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading salary requests...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Summary Statistics - Moved to top */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
            <Typography variant="h4" color="primary" fontWeight="bold">{stats.total}</Typography>
            <Typography variant="body2">Total Requests</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdf4' }}>
            <Typography variant="h4" color="success.main" fontWeight="bold">{stats.approved}</Typography>
            <Typography variant="body2">Approved</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fffbeb' }}>
            <Typography variant="h4" color="warning.main" fontWeight="bold">{stats.pending}</Typography>
            <Typography variant="body2">Pending</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fef2f2' }}>
            <Typography variant="h4" color="error.main" fontWeight="bold">{stats.rejected}</Typography>
            <Typography variant="body2">Rejected</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Single Pie Chart in the middle */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} display="flex" justifyContent="center">
          <Card sx={{ width: "500px", height: "400px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2, textAlign: 'center' }}>
                Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Requests Section Header */}
      <Typography variant="h5" fontWeight="600" sx={{ mb: 3,  }}>
        Pending Salary Requests
      </Typography>

      {/* Filters - Only search now */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by employee name, ID, or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              {filteredRequests.length} of {pendingRequests.length} pending salary requests
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Pending Salary Requests Table */}
      <TableContainer component={Paper} sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Table>
          <TableHead sx={{ bgcolor: "#f8f9fa" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Salary Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Request Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRequests.length > 0 ? (
              paginatedRequests.map((request, index) => (
                <TableRow key={`salary-${request.id}`} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2,  bgcolor: '#e3f2fd', color: '#1565c0', width: 32, height: 32 }}>
                        <Person sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {request.employeeName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {request.employeeId || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                     
                      <Typography variant="body2" fontWeight={500}>
                        ₹{request.salaryAmount?.toLocaleString() || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.department || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.requestDate ? new Date(request.requestDate).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(request.status)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No pending salary requests found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRequests.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  )
}

export default AdvanceSalarySection